import { useState } from 'react';
import type { ToolCallEntry } from '../stores/appStore';
import MarkdownRenderer from './MarkdownRenderer';

interface ToolCallSectionProps {
  entries: ToolCallEntry[];
  isCommitted?: boolean;
  title?: string; // Override toggle label (e.g. "// Agent Output")
  isActive?: boolean; // Pulse the label when true
  defaultExpanded?: boolean; // Start expanded (e.g. live tool calls)
}

const TRUNCATE_LIMIT = 50_000;

function formatJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2).replace(/\\"/g, '"').replace(/\\n/g, '\n');
  } catch {
    return String(value);
  }
}

function paramsPreview(params: unknown, limit = 80): string | null {
  if (!params) return null;
  let obj: Record<string, unknown>;
  if (typeof params === 'string') {
    try { obj = JSON.parse(params); } catch { return params.length > limit ? params.slice(0, limit) + '…' : params; }
  } else if (typeof params === 'object') {
    obj = params as Record<string, unknown>;
  } else {
    return null;
  }
  const parts: string[] = [];
  for (const [k, v] of Object.entries(obj)) {
    const val = typeof v === 'string' ? v : JSON.stringify(v);
    parts.push(`${k}: ${val}`);
  }
  const joined = parts.join('  ');
  if (!joined) return null;
  return joined.length > limit ? joined.slice(0, limit) + '…' : joined;
}

function formatDuration(startedAt?: number, completedAt?: number): string | null {
  if (!startedAt || !completedAt) return null;
  const ms = completedAt - startedAt;
  return `${(ms / 1000).toFixed(1)}s`;
}

function JsonBlock({ label, value, color }: { label: string; value: unknown; color?: string }) {
  const raw = formatJson(value);
  const isTruncated = raw.length > TRUNCATE_LIMIT;
  const [expanded, setExpanded] = useState(false);
  const display = isTruncated && !expanded ? raw.slice(0, TRUNCATE_LIMIT) + '…' : raw;

  return (
    <div className="mt-1.5">
      <div className="mb-0.5 font-semibold" style={color ? { color } : undefined}>
        {label}
      </div>
      <pre
        className="thinking-content max-h-40 overflow-y-auto rounded-md bg-background/50 p-2 text-[11px]"
        style={color ? { color } : undefined}
      >
        {display}
      </pre>
      {isTruncated && (
        <button
          type="button"
          className="mt-0.5 text-[10px] text-accent-foreground underline"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

/** Clean tool result text for markdown rendering:
 *  - Strip Read-tool line number prefixes ("     1→", "  42→")
 *  - Wrap contiguous XML-like blocks in code fences */
function cleanToolResult(text: string): string {
  const stripped = text.replace(/^ *\d+→/gm, '');
  // Wrap contiguous blocks of XML-like lines in code fences
  const lines = stripped.split('\n');
  const result: string[] = [];
  let inXml = false;
  for (const line of lines) {
    const isXmlLine = /^\s*<\/?[\w]/.test(line);
    if (isXmlLine && !inXml) {
      inXml = true;
      result.push('```xml');
    } else if (!isXmlLine && inXml) {
      inXml = false;
      result.push('```');
    }
    result.push(line);
  }
  if (inXml) result.push('```');
  return result.join('\n');
}

function ResultBlock({ value }: { value: unknown }) {
  const isString = typeof value === 'string';
  const raw = isString ? value : formatJson(value);
  const cleaned = isString ? cleanToolResult(raw) : raw;
  const isTruncated = cleaned.length > TRUNCATE_LIMIT;
  const [expanded, setExpanded] = useState(false);
  const display = isTruncated && !expanded ? cleaned.slice(0, TRUNCATE_LIMIT) + '…' : cleaned;

  return (
    <div className="mt-1.5">
      <div className="mb-0.5 font-semibold">Result</div>
      <div className="tool-result-content max-h-60 overflow-y-auto rounded-md bg-background/50 p-2 text-[11px]">
        {isString ? (
          <MarkdownRenderer content={display} />
        ) : (
          <pre className="thinking-content whitespace-pre-wrap">{display}</pre>
        )}
      </div>
      {isTruncated && (
        <button
          type="button"
          className="mt-0.5 text-[10px] text-accent-foreground underline"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: ToolCallEntry['status'] }) {
  const monoStyle = { fontFamily: 'var(--app-ui-font)', fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' as const };

  if (status === 'running') {
    return (
      <span className="inline-flex items-center gap-1" style={{ ...monoStyle, color: '#FF7B35' }}>
        <span className="tool-running-dot" style={{ background: '#FF7B35' }} />
        Doing
      </span>
    );
  }

  if (status === 'error') {
    return <span style={{ ...monoStyle, color: '#fca5a5' }}>✗ Error</span>;
  }

  return <span style={{ ...monoStyle, color: '#3DFFA8' }}>✓ Done</span>;
}

export default function ToolCallSection({ entries, isCommitted, title, isActive, defaultExpanded }: ToolCallSectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded ?? false);

  if (entries.length === 0 && !title) {
    return null;
  }

  const activeCount = entries.filter((e) => e.status === 'running').length;
  const hasActive = isActive ?? activeCount > 0;
  const label = title ?? `// Tool calls (${entries.length})`;

  const entryList = (
    <div className="tool-call-list space-y-2 pl-3">
      {entries.map((entry) => {
        const duration = formatDuration(entry.startedAt, entry.completedAt);
        const preview = paramsPreview(entry.params);

        return (
          <details
            key={entry.toolCallId}
            open={false}
            className="tool-call-card rounded-lg px-3 py-2 text-xs"
            style={{
              background: 'var(--bg-2, #10131E)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderLeft: entry.status === 'done' ? '2px solid rgba(61, 255, 168, 0.35)' : entry.status === 'error' ? '2px solid rgba(255, 70, 70, 0.35)' : '2px solid rgba(255, 123, 53, 0.35)',
            }}
          >
            <summary className="tool-call-summary cursor-pointer list-none">
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="tool-call-chevron text-[10px]" style={{ color: 'var(--theme-primary)' }}>▶</span>
                  <span className="shrink-0 text-foreground" style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    {entry.name}
                    {duration && (
                      <span className="ml-2" style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.6rem', color: 'var(--theme-muted-foreground)', fontWeight: 400 }}>({duration})</span>
                    )}
                  </span>
                  {preview && (
                    <span className="truncate" style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.6rem', color: 'var(--theme-muted-foreground)', fontWeight: 400, textTransform: 'none' }}>
                      {preview}
                    </span>
                  )}
                </div>
                <StatusBadge status={entry.status} />
              </div>
            </summary>
            <div className="tool-call-details-panel">
              <div className="tool-call-details-inner">
                <div className="mt-2 border-t border-input pt-2 text-[11px] text-secondary-foreground">
                  {entry.params && Object.keys(entry.params).length > 0 && (
                    <JsonBlock label="Params" value={entry.params} />
                  )}
                  {entry.status === 'done' && entry.result != null && (
                    <ResultBlock value={entry.result} />
                  )}
                  {entry.status === 'error' && entry.error && (
                    <JsonBlock
                      label="Error"
                      value={entry.error.message || 'Unknown error'}
                      color="var(--theme-destructive-foreground, #ef4444)"
                    />
                  )}
                </div>
              </div>
            </div>
          </details>
        );
      })}
    </div>
  );

  const shouldWrapInToggle = isCommitted || Boolean(title) || !defaultExpanded;
  if (!shouldWrapInToggle) {
    return <div className="mt-1">{entryList}</div>;
  }

  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 transition-colors"
        style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--theme-muted-foreground)' }}
      >
        <span
          className="tool-call-chevron"
          style={{ fontSize: '0.55rem', color: 'var(--theme-primary)', transform: expanded ? 'rotate(90deg)' : undefined }}
        >
          ▶
        </span>
        <span className={hasActive ? 'thinking-pulse' : undefined}>
          {label}
        </span>
      </button>
      <div
        className="tool-call-collapse"
        data-expanded={expanded ? 'true' : 'false'}
        aria-hidden={!expanded}
      >
        <div className="tool-call-collapse-inner pt-2">{entryList}</div>
      </div>
    </div>
  );
}
